'use client';

import { useAuth } from '@clerk/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

/**
 * Clears the React Query cache whenever the active organization changes.
 *
 * The browser QueryClient is a long-lived singleton keyed by tRPC path+input
 * (not by org). Org switches navigate client-side (Clerk's setActive +
 * redirectUrl), so the same client survives the switch and would keep serving
 * the previous org's data until staleTime or a reload (CL-208). Clearing on
 * orgId change drops that stale cross-org data immediately.
 */
export const OrgCacheReset = () => {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const previousOrgId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    // Skip the first run so we never clear a freshly hydrated cache.
    if (previousOrgId.current !== undefined && previousOrgId.current !== orgId) {
      queryClient.clear();
    }
    previousOrgId.current = orgId;
  }, [orgId, queryClient]);

  return null;
};
